import React from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Award, Rocket, Users, Check, Sparkles } from 'lucide-react';

const About = () => {
  const handlePricingLinkClick = () => {
    window.scrollTo(0, 0);
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="text-center mb-16 max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
          Empowering Founders with AI-Powered App Development
        </h1>
        <div className="w-24 h-1 bg-orange-500 mx-auto mb-8"></div>
        <div className="prose prose-lg max-w-none">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            At AppLaunchAI, we believe that building a successful mobile app shouldn't be limited to those with deep technical expertise or massive budgets. Our mission is simple: to level the playing field and make mobile app startups accessible to everyone—from first-time entrepreneurs to seasoned business owners—by harnessing the power of AI-driven development.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
        <div className="order-2 md:order-1">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-xl shadow-lg border border-blue-100 text-left">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Our Mission</h2>
            <p className="text-gray-700 mb-6">
              With decades of combined experience in software development, startup launching, marketing, and branding, our team has guided countless founders through the complex process of turning an idea into a thriving app. We've seen firsthand the challenges of high development costs, long timelines, and technical roadblocks that hold innovators back.
            </p>
            <p className="text-gray-700">
              That's why we created AppLaunchAI—a step-by-step system designed to simplify, automate, and accelerate app creation using cutting-edge AI tools.
            </p>
          </div>
        </div>
        <div className="order-1 md:order-2">
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-blue-100 rounded-full opacity-60"></div>
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-purple-100 rounded-full opacity-60"></div>
            <div className="relative bg-white p-6 rounded-lg shadow-lg">
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c"
                alt="Team collaboration" 
                className="w-full h-auto rounded-lg"
                style={{ minHeight: "300px", objectFit: "cover" }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
        <div>
          <div className="relative">
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-orange-100 rounded-full opacity-60"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-green-100 rounded-full opacity-60"></div>
            <div className="relative bg-white p-6 rounded-lg shadow-lg">
              <img 
                src="https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7"
                alt="AI Development" 
                className="w-full h-auto rounded-lg"
                style={{ minHeight: "300px", objectFit: "cover" }}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-8 rounded-xl shadow-lg border border-orange-100 text-left">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Our Approach</h2>
            <p className="text-gray-700 mb-6">
              Whether you're looking to build your first app or optimize an existing idea, our program equips you with everything you need—from AI-powered design and development to growth strategies, monetization insights, and marketing expertise.
            </p>
            <p className="text-gray-700">
              With our guidance, you can go from concept to launch faster and more affordably than ever before. At AppLaunchAI, we're not just teaching app development—we're revolutionizing it.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-10 text-white shadow-xl">
        <h2 className="text-2xl font-bold mb-6 text-center">Why Choose AppLaunchAI?</h2>
        <div className="grid md:grid-cols-3 gap-8 mt-8">
          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg shadow-lg border border-white/20">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
              <Rocket className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-center mb-2">Speed to Market</h3>
            <p className="text-white/80 text-center">
              Launch your app 5x faster using our AI-driven development process and proven templates.
            </p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg shadow-lg border border-white/20">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
              <Award className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-center mb-2">Expertise</h3>
            <p className="text-white/80 text-center">
              Benefit from decades of combined experience in successful app development and marketing.
            </p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg shadow-lg border border-white/20">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-center mb-2">Innovation</h3>
            <p className="text-white/80 text-center">
              Stay ahead of the curve with cutting-edge AI tools and strategies used by top tech startups.
            </p>
          </div>
        </div>
        
        <div className="text-center mt-12">
          <Link to="/pricing" onClick={handlePricingLinkClick}>
            <Button size="lg" className="bg-white hover:bg-white/90 text-blue-700">
              Explore Our Plans
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default About;
